import { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

/**
 * Hook to display real-time duration counter for an activity
 * Format: mm:ss or h:mm:ss if duration > 60 minutes
 *
 * @param {string} startedAt - ISO timestamp of when activity started
 * @param {boolean} isRunning - Whether the activity is currently running
 * @returns {string} Formatted duration string (e.g., "05:30", "1:23:45")
 */
export const useActivityTimer = (startedAt, isRunning) => {
  const [displayTime, setDisplayTime] = useState('00:00');

  useEffect(() => {
    if (!isRunning || !startedAt) {
      setDisplayTime('00:00');
      return;
    }

    const updateTimer = () => {
      const start = dayjs(startedAt);
      const now = dayjs();
      const diffInSeconds = now.diff(start, 'seconds');

      const hours = Math.floor(diffInSeconds / 3600);
      const minutes = Math.floor((diffInSeconds % 3600) / 60);
      const seconds = diffInSeconds % 60;

      let formattedTime;
      if (hours > 0) {
        // Format: h:mm:ss
        formattedTime = `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      } else {
        // Format: mm:ss
        formattedTime = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
      }

      setDisplayTime(formattedTime);
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [startedAt, isRunning]);

  return displayTime;
};
